import { type FC, type ReactNode } from 'react';

interface ProfileCardProps {
  children: ReactNode;
  title: string;
}

/**
 * The frame's card: a quiet 13/18 heading over its contents, in a bordered
 * 16-radius box. Both sections of the page are one of these.
 *
 * The heading is a `<p>`. It labels the card for a sighted reader, and what it
 * labels is already named — the rows are a description list. A heading here
 * would add stops to the document outline that lead nowhere.
 */
export const ProfileCard: FC<ProfileCardProps> = ({ children, title }) => (
  <section className="flex flex-col gap-4 rounded-2xl border border-[var(--ds-color-border-primary)] p-6">
    <p className="text-[13px] leading-[18px] text-[color:var(--ds-color-raw-stone-600)]">{title}</p>
    {children}
  </section>
);

export interface ProfileDetail {
  label: string;
  value: string;
}

/**
 * The label/value rows inside a card.
 *
 * A description list, because that is what they are. The frame's two columns are
 * both `flex-1`, so the values line up with each other rather than sitting
 * against their labels.
 */
export const ProfileDetailList: FC<{ details: ProfileDetail[] }> = ({ details }) => (
  <dl className="flex flex-col gap-4 text-sm leading-5">
    {details.map(({ label, value }) => (
      <div className="flex items-center" key={label}>
        <dt className="flex-1 font-medium text-[var(--ds-color-text-primary)]">{label}</dt>
        <dd className="flex-1 text-[color:var(--ds-color-raw-stone-600)]">{value}</dd>
      </div>
    ))}
  </dl>
);
